import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import useSWR from 'swr';
import { CardContent, Box, Typography, Avatar, Chip, Divider, Paper, Card } from '@mui/material';
import { Person, Alarm, Send, Business } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { isToday, isPast } from 'date-fns';

import { expertApi } from 'src/api/expertApi';
import useFormatDate from 'src/hooks/useFormatDate';
import { useDecodedToken } from 'src/hooks/useDecodedToken';
import { QUERY_STATUSES } from 'src/constants/config';
import { toTitleCase, trimNameToThreeWords } from 'src/utils/string';
import QueryStatus from 'src/pages/QueryPages/components/QueryStatus';
import QueryCardActions from 'src/pages/QueryPages/components/QueryCardActions';
import CompanySelect from 'src/pages/QueryPages/components/CompanySelect';

const isDeadlineTodayOrPassed = (dueDate) => {
  const date = new Date(dueDate);
  return isToday(date) || isPast(date);
};

const expertsFetcher = () => expertApi.getExperts();

const QueryCard = ({ query, users, path, mutate, showDeadline = true }) => {
  const { data: experts } = useSWR('experts', expertsFetcher);
  const { isExpert } = useDecodedToken();

  // Save scroll position before navigating to query profile
  const handleCardClick = () => {
    const scrollY = window.scrollY;
    sessionStorage.setItem('queryPageScrollPosition', scrollY.toString());
  };

  const [clientName, setClientName] = useState('Unknown Client');
  const [clientCompany, setClientCompany] = useState('Unknown Company');
  const [sentToClientDate, setSentToClientDate] = useState('Unknown date');

  const formattedClientDate = useFormatDate(sentToClientDate);
  const formattedDueDate = useFormatDate(query?.dueDate);
  const formattedReporterDate = useFormatDate(query?.sentToReporterDate);

  useEffect(() => {
    if (query.status !== 'new' && query.status !== 'draft') {
      const clientExpert = experts?.find((expert) => expert.id === query.expertID);
      const clientExpertPreferred = experts?.find((expert) => expert.id === Number(query.expertsID));

      const expert = clientExpertPreferred || clientExpert;

      setClientName(expert ? trimNameToThreeWords(expert.name) : 'Unknown Client');
      if (expert?.company) {
        setClientCompany(expert?.position + ' at ' + expert?.company);
      } else {
        setClientCompany('Unknown Company');
      }
      setSentToClientDate(query?.sentToExpertDate || 'Unknown Date');
    }
  }, [query, experts]);

  const deadlineStyle = isDeadlineTodayOrPassed(query.dueDate) ? { color: 'error.main', fontWeight: 'bold' } : { color: 'text.primary' };

  return (
    <RouterLink to={path} style={{ textDecoration: 'none', color: 'inherit' }} onClick={handleCardClick}>
      <Card
        sx={{
          position: 'relative',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          boxShadow: 3,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 6,
          },
        }}
      >
        <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Title Section */}
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              height: '65px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 4,
              WebkitBoxOrient: 'vertical',
              color: '#0A0E13',
            }}
          >
            {query.title}
          </Typography>

          {/* Media Outlet Section with improved styling */}
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              mb: 2,
              display: 'flex',
              flexWrap: 'nowrap',
              alignItems: 'center',
              backgroundColor: 'background.default',
              borderRadius: 1.5,
              overflow: 'hidden',
              width: '100%',
            }}
          >
            <Avatar
              src={query.qwotedImage}
              sx={{
                width: 36,
                height: 36,
                mr: 1.5,
                flexShrink: 0,
                '& img': {
                  objectFit: 'contain',
                  backgroundColor: 'white',
                },
                border: '1px solid',
                borderColor: 'divider',
              }}
            />
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
              }}
            >
              {toTitleCase(query.mediaOutlet)}
            </Typography>
          </Paper>

          {/* Client Information Section */}
          {query.status === 'draft' && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Business sx={{ fontSize: 18, mr: 1, mt: 0.3, color: 'text.primary' }} />
                <Box sx={{ flexGrow: 1 }}>
                  <CompanySelect query={query} mutate={mutate} />
                </Box>
              </Box>
            </Box>
          )}

          {query.status !== 'new' && query.status !== 'draft' && query.status !== 'discarded' && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Person sx={{ fontSize: 20, mr: 1, color: 'primary.main' }} />
                <Typography variant="body1" fontWeight={500} color="primary.main">
                  {clientName}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start', ml: 0.25 }}>
                <Business sx={{ fontSize: 18, mr: 1, mt: 0.3, color: 'text.primary' }} />
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{
                    overflow: 'hidden',
                    height: '40px',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    lineHeight: 1.4,
                  }}
                >
                  {clientCompany}
                </Typography>
              </Box>
            </Box>
          )}

          <Divider sx={{ my: 1.5 }} />

          {/* Date Information Section */}
          <Box sx={{ mt: 'auto' }}>
            {query.status !== 'new' && query.status !== 'draft' && query.status !== 'discarded' && !isExpert && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Send sx={{ fontSize: 18, mr: 1, color: 'black' }} />
                <Typography variant="body2" color="text.primary">
                  Sent to Client: {sentToClientDate ? formattedClientDate : 'No date'}
                </Typography>
              </Box>
            )}

            {showDeadline ? (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Alarm sx={{ fontSize: 18, mr: 1, ...deadlineStyle }} />
                <Typography
                  variant="body2"
                  sx={{
                    ...deadlineStyle,
                  }}
                >
                  Due: {formattedDueDate}
                </Typography>

                {isDeadlineTodayOrPassed(query.dueDate) && (
                  <Chip
                    size="small"
                    label={isToday(new Date(query.dueDate)) ? 'Today' : 'Overdue'}
                    color="error"
                    variant="outlined"
                    sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                  />
                )}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Send sx={{ fontSize: 18, mr: 1, color: 'black' }} />
                <Typography variant="body2" color="text.primary">
                  Sent to Reporter: {query?.sentToReporterDate ? formattedReporterDate : 'Not sent yet'}
                </Typography>
              </Box>
            )}
            {!isExpert && (
              <Box sx={{ mb: 2 }}>
                <QueryStatus query={query} mutate={mutate} />
              </Box>
            )}

            {isExpert && (
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    mr: 1,
                  }}
                >
                  Status:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color:
                      QUERY_STATUSES[query.status]?.color === 'success' ? 'white' : `${QUERY_STATUSES[query.status]?.color || 'text.primary'}.main`,
                    fontWeight: 600,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    backgroundColor: (theme) =>
                      QUERY_STATUSES[query.status]?.color === 'success'
                        ? theme.palette.success.main
                        : `${QUERY_STATUSES[query.status]?.color || 'text.primary'}.light`,
                    display: 'inline-block',
                    opacity: 0.9,
                  }}
                >
                  {QUERY_STATUSES[query.status]?.label || query.status}
                </Typography>
              </Box>
            )}

            <QueryCardActions query={query} users={users} mutate={mutate} />
          </Box>
        </CardContent>
      </Card>
    </RouterLink>
  );
};

QueryCard.propTypes = {
  query: PropTypes.object.isRequired,
  users: PropTypes.array.isRequired,
  path: PropTypes.string.isRequired,
  mutate: PropTypes.func.isRequired,
  showDeadline: PropTypes.bool,
};

export default QueryCard;
